import { Button } from "@/ui/button";
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/ui/item";
import { ScrollArea } from "@/ui/scroll-area";
import { useAcoes, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { CortinaLivre, Fechar } from "../componentes/comuns.jsx";
import { urgencia } from "../avisos.js";

/**
 * O sino aberto: prazo curto, SLA estourando e o que o copiloto mexeu.
 *
 * Quem abre e quem fecha continua sendo a URL (`?avisos=`) — é recorte da mesma
 * tela, e recarregar tem de cair no mesmo lugar. Por baixo é o `Dialog` do
 * shadcn (via `CortinaLivre`): antes só o ✕ fechava a caixa; agora Esc e clique
 * fora também, e o foco não escapa para a lista de conversas atrás dela.
 *
 * NÃO é um `Sheet`: o sino não entra pela borda da tela, ele NASCE DO SINO — que
 * hoje está no ALTO DO TRILHO, à esquerda, e por isso o balão se encosta na
 * largura dele (`--trilho`, que a casca escreve e muda quando o trilho é
 * fixado) — e tem a altura do que couber. `Sheet` é painel encostado na borda, de altura
 * cheia: a caixa mudaria de lugar e de tamanho, e o que ela é é balão do sino.
 *
 * O balão é PRÉVIA, não arquivo: doze linhas de 70px crescendo a partir de
 * `bottom` passavam do topo da janela e as últimas ficavam fora do alcance —
 * agora a lista rola dentro dele e o rodapé leva para `/notificacoes`, que é
 * onde cabe filtrar e ler tudo.
 */
export default function CaixaAvisos() {
  const { avisos } = useEstado();
  const { marcarAvisosLidos } = useAcoes();
  const nav = useNavegacao();
  const fechar = () => nav.ajustar({ avisos: null });
  /* Sair do balão para outro lugar é UMA navegação, já sem `?avisos=1` — senão
     o balão reabre por cima do destino (ver `buscaSem` em navegacao.jsx). */
  const irFechando = (caminho) => nav.ir(`${caminho}${nav.buscaSem("avisos")}`);

  return (
    <CortinaLivre
      aoFechar={fechar}
      rotulo="Notificações"
      z={30}
      fundo="transparent"
      moldura="position:fixed;left:calc(var(--trilho, 56px) + 8px);top:12px"
      estilo="width:min(330px,calc(100vw - 24px));background:var(--color-papel);border:1px solid color-mix(in oklab, var(--color-foreground) 13%, transparent);border-radius:16px;box-shadow:0 22px 50px color-mix(in oklab, var(--color-foreground) 22%, transparent);overflow:hidden;animation:vFade var(--t-curta) var(--e-entra) both;display:flex;flex-direction:column;max-height:min(70vh,520px)"
    >
      <div className="flex flex-none items-center justify-between p-[14px_16px_10px]">
        <span className="font-serif text-[18px]">
          Notificações
          {avisos.length > 0 && <span className="ml-[7px] font-mono text-selo text-muted-foreground">{avisos.length}</span>}
        </span>
        <span className="flex items-center gap-2">
          {/* Zerar o sino: sem isto o aviso ficava para sempre — ele é conta, não
              linha de banco, e não havia o que marcar como lido. */}
          {avisos.length > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => marcarAvisosLidos(avisos)}
              className="h-auto px-1 py-0.5 text-selo font-bold tracking-[.02em] text-verde-fundo"
            >
              Marcar como lidas
            </Button>
          )}
          <Fechar
            aoTocar={fechar}
            titulo="Fechar as notificações"
            lado={22}
            tom="color-mix(in oklab, var(--color-foreground) 7%, transparent)"
          />
        </span>
      </div>

      {/*
       * A lista é o que rola; cabeçalho e rodapé ficam parados.
       *
       * A altura é DEFINIDA (`max-h`), e não `flex-1`: a área de rolagem do
       * shadcn põe o conteúdo num viewport `height:100%`, e 100% de um pai que
       * mede pelo conteúdo é "automático" — a lista crescia por baixo do rodapé
       * e o balão cortava tudo em 520px, sem barra de rolagem. Com o teto aqui, o
       * viewport tem contra o que medir. Vale para todo `ScrollArea` da mesa.
       */}
      <ScrollArea className="max-h-[min(60vh,420px)]">
        {avisos.length ? (
          avisos.map((aviso, i) => {
            /* `link` e não `record_id`: quem sabe onde o aviso se resolve é quem
               o montou (`src/copiloto.ts`), como já era com a pendência. Prazo
               sem caso vinculado ia para `/tarefas?tarefa=<id>` e a tela o
               desenhava morto — a tarefa existe. */
            const abre = Boolean(aviso.link ?? aviso.record_id);
            return (
              /* `Item` do shadcn: a bolinha da cor é a mídia, título e corpo são
                 os slots de sempre. Aqui ele é linha rente à borda do balão, sem
                 raio nem moldura própria — a separação é o fio de cima. */
              <Item
                key={`${aviso.titulo}-${aviso.quando}-${i}`}
                size="sm"
                render={<button type="button" />}
                /* Abrir o caso É ter visto o aviso: ele sai do sino junto. */
                onClick={() => {
                  if (!abre) return;
                  marcarAvisosLidos([aviso]);
                  irFechando(aviso.link ?? `/atendimentos/${aviso.record_id}`);
                }}
                /* Aviso sem destino não vai a lugar nenhum: mão de clique e foco
                   de teclado num botão inerte é promessa que a tela não cumpre. */
                disabled={!abre}
                className={`w-full items-start gap-[11px] rounded-none border-t border-t-foreground/7 p-[11px_16px] text-left ${
                  abre ? "lin" : "cursor-default"
                }`}
              >
                <ItemMedia className="mt-[5px] self-start">
                  <span className="block size-[7px] flex-none rounded-[9px]" style={{ background: aviso.cor }} />
                </ItemMedia>
                <ItemContent className="gap-0.5">
                  <ItemTitle className="block text-corpo leading-[1.4] font-semibold whitespace-normal">{aviso.titulo}</ItemTitle>
                  <ItemDescription className="text-apoio leading-[1.45] text-tinta-suave">
                    {/* O cliente é campo próprio do aviso desde que virou a
                        manchete da página de Notificações — aqui ele entra na
                        frente da ação, que é como a linha era lida antes de o
                        nome sair de dentro de `corpo`. */}
                    {aviso.cliente ? `${aviso.cliente} — ${aviso.corpo}` : aviso.corpo}
                  </ItemDescription>
                  {/* A urgência em palavras ("venceu há 3 dias", "espera há 40
                      min"), pelo mesmo cálculo da página: o balão mostrava a
                      data seca e quem lê o sino quer saber se aperta. */}
                  <span className="mt-0.5 block font-mono text-selo text-muted-foreground">{urgencia(aviso).frase}</span>
                </ItemContent>
              </Item>
            );
          })
        ) : (
          <div className="p-4 text-corpo text-muted-foreground">Nada pendente agora.</div>
        )}
      </ScrollArea>

      <Button
        variant="ghost"
        onClick={() => irFechando("/notificacoes")}
        className="h-auto w-full flex-none rounded-none border-t border-t-foreground/9 bg-secondary p-[11px_16px] text-apoio font-semibold text-verde-fundo"
      >
        Ver todas as notificações
      </Button>
    </CortinaLivre>
  );
}
